import React, { useState, useEffect } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { Layout } from '../../components/layout/Layout';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Textarea } from '../../components/ui/textarea';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../../components/ui/card';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../../components/ui/select';
import { Switch } from '../../components/ui/switch';
import { Label } from '../../components/ui/label';
import { toast } from 'sonner';
import { 
    ArrowLeft,
    Briefcase,
    MapPin,
    Building2,
    PoundSterling,
    Calendar,
    Save,
    Loader2,
    Eye,
    FileText
} from 'lucide-react';

const AddJobPage = () => {
    const navigate = useNavigate();
    const { jobId } = useParams();
    const isEditing = Boolean(jobId);
    
    const [loading, setLoading] = useState(false);
    const [saving, setSaving] = useState(false);
    const [agencies, setAgencies] = useState([]);
    
    const [formData, setFormData] = useState({
        agency_id: '',
        agency_name: '',
        title: '',
        description: '',
        location: '',
        country: 'UK',  // Add country field
        job_type: 'full-time',
        salary_min: '',
        salary_max: '',
        salary_period: 'year',
        experience_level: 'mid',
        apply_url: '',
        expires_at: '',
        is_featured: false,
        is_active: true
    });

    useEffect(() => {
        fetchAgencies();
        if (isEditing) {
            fetchJob();
        }
    }, [jobId]);

    const fetchAgencies = async () => {
        try {
            const response = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/admin/agencies?limit=500`, {
                headers: {
                    'Authorization': `Bearer ${localStorage.getItem('session_token')}`
                }
            });
            if (response.ok) {
                const data = await response.json();
                setAgencies(data.agencies || []);
            }
        } catch (error) {
            console.error('Failed to fetch agencies:', error);
        }
    };

    const fetchJob = async () => {
        setLoading(true);
        try {
            const response = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/jobs/${jobId}`, {
                headers: {
                    'Authorization': `Bearer ${localStorage.getItem('session_token')}`
                }
            });
            if (response.ok) {
                const job = await response.json();
                setFormData({
                    agency_id: job.agency_id || '',
                    agency_name: job.agency_name || '',
                    title: job.title || '',
                    description: job.description || '',
                    location: job.location || '',
                    job_type: job.job_type || 'full-time',
                    salary_min: job.salary_min?.toString() || '',
                    salary_max: job.salary_max?.toString() || '',
                    salary_period: job.salary_period || 'year',
                    experience_level: job.experience_level || 'mid',
                    apply_url: job.apply_url || '',
                    expires_at: job.expires_at ? job.expires_at.split('T')[0] : '',
                    is_featured: job.is_featured || false,
                    is_active: job.is_active !== false
                });
            }
        } catch (error) {
            toast.error('Failed to fetch job details');
        } finally {
            setLoading(false);
        }
    };

    const handleSubmit = async (e) => {
        e.preventDefault();
        setSaving(true);
        
        try {
            const payload = {
                ...formData,
                salary_min: formData.salary_min ? parseInt(formData.salary_min) : null,
                salary_max: formData.salary_max ? parseInt(formData.salary_max) : null,
                expires_at: formData.expires_at || null
            };

            const url = isEditing 
                ? `${process.env.REACT_APP_BACKEND_URL}/api/admin/jobs/${jobId}`
                : `${process.env.REACT_APP_BACKEND_URL}/api/admin/jobs`;
            
            const response = await fetch(url, {
                method: isEditing ? 'PUT' : 'POST',
                headers: {
                    'Content-Type': 'application/json',
                    'Authorization': `Bearer ${localStorage.getItem('session_token')}`
                },
                body: JSON.stringify(payload)
            });

            if (response.ok) {
                toast.success(isEditing ? 'Job updated successfully!' : 'Job created successfully!');
                navigate('/admin?tab=jobs');
            } else {
                const error = await response.json();
                toast.error(error.detail || 'Failed to save job');
            }
        } catch (error) {
            toast.error('An error occurred while saving');
        } finally {
            setSaving(false);
        }
    };

    const handleInputChange = (field, value) => {
        setFormData(prev => ({ ...prev, [field]: value }));
    };

    if (loading) {
        return (
            <Layout>
                <div className="flex items-center justify-center min-h-[60vh]">
                    <Loader2 className="w-8 h-8 animate-spin text-primary-600" />
                </div>
            </Layout>
        );
    }

    return (
        <Layout>
            <div className="bg-neutral-50 min-h-screen py-8">
                <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
                    {/* Header */}
                    <div className="mb-8">
                        <Button 
                            variant="ghost" 
                            onClick={() => navigate('/admin?tab=jobs')}
                            className="mb-4"
                        >
                            <ArrowLeft className="w-4 h-4 mr-2" />
                            Back to Jobs
                        </Button>
                        <h1 className="text-3xl font-bold text-neutral-900">
                            {isEditing ? 'Edit Job Post' : 'Create New Job Post'}
                        </h1>
                        <p className="text-neutral-600 mt-2">
                            {isEditing ? 'Update the job posting details below' : 'Fill in the details to create a new job posting'}
                        </p>
                    </div>

                    <form onSubmit={handleSubmit} className="space-y-6">
                        {/* Basic Information */}
                        <Card>
                            <CardHeader>
                                <CardTitle className="flex items-center gap-2">
                                    <Briefcase className="w-5 h-5" />
                                    Basic Information
                                </CardTitle>
                                <CardDescription>
                                    Enter the core details about this job position
                                </CardDescription>
                            </CardHeader>
                            <CardContent className="space-y-4">
                                {/* Agency Selection */}
                                <div>
                                    <Label htmlFor="agency" className="text-sm font-medium">
                                        Agency <span className="text-red-500">*</span>
                                    </Label>
                                    <div className="relative mt-1">
                                        <Input
                                            id="agency_name"
                                            placeholder="Type agency name or select from list"
                                            value={formData.agency_name || ''}
                                            onChange={(e) => {
                                                const typed = e.target.value;
                                                handleInputChange('agency_name', typed);
                                                // If typed value matches an existing agency exactly, link to its id;
                                                // otherwise treat as a custom free-text agency name
                                                const match = agencies.find(a => a.name && a.name.toLowerCase() === typed.toLowerCase());
                                                handleInputChange('agency_id', match ? match.agency_id : '');
                                            }}
                                            list="agencies-datalist"
                                            className="w-full"
                                            data-testid="job-agency-name-input"
                                        />
                                        <datalist id="agencies-datalist">
                                            {agencies.map((agency) => (
                                                <option key={agency.agency_id} value={agency.name} data-id={agency.agency_id} />
                                            ))}
                                        </datalist>
                                    </div>
                                    <p className="text-xs text-neutral-500 mt-1">
                                        You can type a custom agency name or select from existing agencies
                                    </p>
                                </div>

                                {/* Job Title */}
                                <div>
                                    <Label htmlFor="title" className="text-sm font-medium">
                                        Job Title <span className="text-red-500">*</span>
                                    </Label>
                                    <Input
                                        id="title"
                                        required
                                        placeholder="e.g. Senior Software Engineer, Marketing Manager"
                                        value={formData.title}
                                        onChange={(e) => handleInputChange('title', e.target.value)}
                                        className="mt-1"
                                    />
                                </div>

                                {/* Description */}
                                <div>
                                    <Label htmlFor="description" className="text-sm font-medium">
                                        Job Description <span className="text-red-500">*</span>
                                    </Label>
                                    <Textarea
                                        id="description"
                                        required
                                        placeholder="Provide a detailed description of the role, including responsibilities, requirements, and what makes this opportunity unique..."
                                        value={formData.description}
                                        onChange={(e) => handleInputChange('description', e.target.value)}
                                        className="mt-1 min-h-[250px]"
                                    />
                                    <p className="text-xs text-neutral-500 mt-1">
                                        {formData.description.length} characters
                                    </p>
                                </div>
                            </CardContent>
                        </Card>

                        {/* Location & Type */}
                        <Card>
                            <CardHeader>
                                <CardTitle className="flex items-center gap-2">
                                    <MapPin className="w-5 h-5" />
                                    Location & Job Type
                                </CardTitle>
                            </CardHeader>
                            <CardContent className="space-y-4">
                                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                                    <div>
                                        <Label htmlFor="location" className="text-sm font-medium">
                                            Location <span className="text-red-500">*</span>
                                        </Label>
                                        <Input
                                            id="location"
                                            required
                                            placeholder="e.g. London, Toronto, Manchester"
                                            value={formData.location}
                                            onChange={(e) => handleInputChange('location', e.target.value)}
                                            className="mt-1"
                                        />
                                    </div>
                                    <div>
                                        <Label htmlFor="country" className="text-sm font-medium">
                                            Country <span className="text-red-500">*</span>
                                        </Label>
                                        <Select
                                            value={formData.country || 'UK'}
                                            onValueChange={(value) => handleInputChange('country', value)}
                                        >
                                            <SelectTrigger className="mt-1">
                                                <SelectValue />
                                            </SelectTrigger>
                                            <SelectContent>
                                                <SelectItem value="UK">🇬🇧 United Kingdom</SelectItem>
                                                <SelectItem value="Canada">🇨🇦 Canada</SelectItem>
                                            </SelectContent>
                                        </Select>
                                    </div>
                                    <div>
                                        <Label className="text-sm font-medium">
                                            Job Type <span className="text-red-500">*</span>
                                        </Label>
                                        <Select
                                            value={formData.job_type}
                                            onValueChange={(value) => handleInputChange('job_type', value)}
                                        >
                                            <SelectTrigger className="mt-1">
                                                <SelectValue />
                                            </SelectTrigger>
                                            <SelectContent>
                                                <SelectItem value="full-time">Full-time</SelectItem>
                                                <SelectItem value="part-time">Part-time</SelectItem>
                                                <SelectItem value="contract">Contract</SelectItem>
                                                <SelectItem value="temporary">Temporary</SelectItem>
                                                <SelectItem value="internship">Internship</SelectItem>
                                            </SelectContent>
                                        </Select>
                                    </div>
                                </div>
                                <div>
                                    <Label className="text-sm font-medium">Experience Level</Label>
                                    <Select
                                        value={formData.experience_level}
                                        onValueChange={(value) => handleInputChange('experience_level', value)}
                                    >
                                        <SelectTrigger className="mt-1">
                                            <SelectValue />
                                        </SelectTrigger>
                                        <SelectContent>
                                            <SelectItem value="entry">Entry Level</SelectItem>
                                            <SelectItem value="mid">Mid Level</SelectItem>
                                            <SelectItem value="senior">Senior Level</SelectItem>
                                            <SelectItem value="executive">Executive</SelectItem>
                                        </SelectContent>
                                    </Select>
                                </div>
                            </CardContent>
                        </Card>

                        {/* Salary */}
                        <Card>
                            <CardHeader>
                                <CardTitle className="flex items-center gap-2">
                                    <PoundSterling className="w-5 h-5" />
                                    Compensation
                                </CardTitle>
                            </CardHeader>
                            <CardContent>
                                <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                                    <div>
                                        <Label htmlFor="salary_min" className="text-sm font-medium">
                                            Minimum Salary (£)
                                        </Label>
                                        <Input
                                            id="salary_min"
                                            type="number"
                                            placeholder="30000"
                                            value={formData.salary_min}
                                            onChange={(e) => handleInputChange('salary_min', e.target.value)}
                                            className="mt-1"
                                        />
                                    </div>
                                    <div>
                                        <Label htmlFor="salary_max" className="text-sm font-medium">
                                            Maximum Salary (£)
                                        </Label>
                                        <Input
                                            id="salary_max"
                                            type="number"
                                            placeholder="50000"
                                            value={formData.salary_max}
                                            onChange={(e) => handleInputChange('salary_max', e.target.value)}
                                            className="mt-1"
                                        />
                                    </div>
                                    <div>
                                        <Label className="text-sm font-medium">Salary Period</Label>
                                        <Select
                                            value={formData.salary_period}
                                            onValueChange={(value) => handleInputChange('salary_period', value)}
                                        >
                                            <SelectTrigger className="mt-1">
                                                <SelectValue />
                                            </SelectTrigger>
                                            <SelectContent>
                                                <SelectItem value="year">Per Year</SelectItem>
                                                <SelectItem value="month">Per Month</SelectItem>
                                                <SelectItem value="week">Per Week</SelectItem>
                                                <SelectItem value="day">Per Day</SelectItem>
                                                <SelectItem value="hour">Per Hour</SelectItem>
                                            </SelectContent>
                                        </Select>
                                    </div>
                                </div>
                            </CardContent>
                        </Card>

                        {/* Additional Details */}
                        <Card>
                            <CardHeader>
                                <CardTitle className="flex items-center gap-2">
                                    <FileText className="w-5 h-5" />
                                    Additional Details
                                </CardTitle>
                            </CardHeader>
                            <CardContent className="space-y-4">
                                <div>
                                    <Label htmlFor="apply_url" className="text-sm font-medium">
                                        Application URL
                                    </Label>
                                    <Input
                                        id="apply_url"
                                        type="url"
                                        placeholder="https://example.com/apply"
                                        value={formData.apply_url}
                                        onChange={(e) => handleInputChange('apply_url', e.target.value)}
                                        className="mt-1"
                                    />
                                    <p className="text-xs text-neutral-500 mt-1">
                                        External link where candidates can apply
                                    </p>
                                </div>
                                <div>
                                    <Label htmlFor="expires_at" className="text-sm font-medium">
                                        Expiry Date
                                    </Label>
                                    <Input
                                        id="expires_at"
                                        type="date"
                                        value={formData.expires_at}
                                        onChange={(e) => handleInputChange('expires_at', e.target.value)}
                                        className="mt-1"
                                        min={new Date().toISOString().split('T')[0]}
                                    />
                                    <p className="text-xs text-neutral-500 mt-1">
                                        Job will be automatically hidden after this date
                                    </p>
                                </div>
                                <div className="flex items-center justify-between p-4 bg-neutral-50 rounded-lg">
                                    <div>
                                        <Label className="text-sm font-medium">Featured Job</Label>
                                        <p className="text-xs text-neutral-500">
                                            Featured jobs appear at the top of search results
                                        </p>
                                    </div>
                                    <Switch
                                        checked={formData.is_featured}
                                        onCheckedChange={(checked) => handleInputChange('is_featured', checked)}
                                    />
                                </div>
                                <div className="flex items-center justify-between p-4 bg-neutral-50 rounded-lg">
                                    <div>
                                        <Label className="text-sm font-medium">Active</Label>
                                        <p className="text-xs text-neutral-500">
                                            Only active jobs are visible to job seekers
                                        </p>
                                    </div>
                                    <Switch
                                        checked={formData.is_active}
                                        onCheckedChange={(checked) => handleInputChange('is_active', checked)}
                                    />
                                </div>
                            </CardContent>
                        </Card>

                        {/* Actions */}
                        <div className="flex items-center justify-end gap-4 pt-4">
                            <Button
                                type="button"
                                variant="outline"
                                onClick={() => navigate('/admin?tab=jobs')}
                            >
                                Cancel
                            </Button>
                            <Button
                                type="submit"
                                disabled={saving}
                                className="bg-primary-600 hover:bg-primary-700"
                            >
                                {saving ? (
                                    <>
                                        <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                                        Saving...
                                    </>
                                ) : (
                                    <>
                                        <Save className="w-4 h-4 mr-2" />
                                        {isEditing ? 'Update Job' : 'Create Job'}
                                    </>
                                )}
                            </Button>
                        </div>
                    </form>
                </div>
            </div>
        </Layout>
    );
};

export default AddJobPage;
